import { useEffect } from "react";

import { PalAutoComplete, PalSelect, PalTextField, PalTypography } from "@palamar/fe-library";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Grid } from "@mui/material";

import PRProfile from "~components/Generic/PRProfile";
import AlertHelper from "~helpers/AlertHelper";
import FileHelper from "~helpers/FileHelper";
import { getProjectLanguages } from "~store/settings/projectSettings/actions";
import { selectProject, selectProjectLanguages } from "~store/settings/projectSettings/selectors";

import { HelpIconWrapper, RequiredComp, StepContainer } from "../CommonComponents";

const IndustryList = [
  { value: "1", label: "Food & Beverage" },
  { value: "2", label: "Health & Wellness" },
  { value: "3", label: "Retail" },
  { value: "4", label: "Real Estate" },
  { value: "5", label: "Automotive" },
  { value: "6", label: "Education" },
  { value: "7", label: "Finance" },
  { value: "8", label: "Hospitality" },
  { value: "9", label: "Technology" },
  { value: "10", label: "Other" },
];

const CompanySizeList = [
  { value: "1", label: "1-10" },
  { value: "2", label: "11-50" },
  { value: "3", label: "51-200" },
  { value: "4", label: "201-500" },
  { value: "5", label: "501-1000" },
  { value: "6", label: "1000+" },
];

const DepartmentList = [
  { value: "1", label: "Marketing" },
  { value: "2", label: "Sales" },
  { value: "3", label: "Customer Service" },
  { value: "4", label: "IT" },
  { value: "5", label: "Other" },
];

const RoleList = [
  { value: "1", label: "Owner" },
  { value: "2", label: "Manager" },
  { value: "3", label: "Employee" },
  { value: "4", label: "Other" },
];

function StepGeneralInformation({ formik, setFieldValue, stepKey, assistantLanguage }) {
  const dispatch = useDispatch();
  const project = useSelector(selectProject);
  const projectLanguages = useSelector(selectProjectLanguages);

  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getProjectLanguages());
  }, [dispatch]);
  // const project = useSelector(selectProject);
  const handleHeaderFileChange = async (file, e) => {
    const img = await FileHelper.getImage(file);
    const width = img.width;
    const height = img.height;
    if (width !== height || width < 144) {
      AlertHelper.showError(t("dashboard.onboarding.logoSizeError"));
      e.preventDefault();
      return;
    }

    // setFiles({ ...files, popup_header_icon_path: [file] });
    setFieldValue("company_logo_file", file);
    const blobUri = URL.createObjectURL(file);
    // dispatch(setPopupSettingsState("popup_header_icon_path", blobUri));
    setFieldValue("company_logo", blobUri);
  };

  const handleChangeSupportedLanguages = (e, value) => {
    setFieldValue("assistant_supported_languages", value);
  };

  // const translatedProjectLanguageOptions = useMemo(() => {
  //   return projectLanguageOptions.map((item) => ({ ...item, label: t(item.label) }));
  // }, [t]);

  const handleChangeTextField = (key) => (e) => {
    setFieldValue(key, e.target.value);
  };

  const handleChangeCompanyDetailsOption = (key) => (e, item) => {
    setFieldValue(`company_details.${key}`, item);
  };

  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            General Information
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please fill out the following information to get started.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Branding Logo
          </PalTypography>
          <PRProfile img={formik.values[stepKey]?.company_logo} onFileChange={handleHeaderFileChange} />
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Supported Languages
          </PalTypography>
          <PalAutoComplete
            fullWidth
            multiple
            label="Enter Supported Languages"
            labelSelector="name"
            options={projectLanguages.filter((i) => i.code?.toLowerCase() !== assistantLanguage)}
            value={formik.values[stepKey].assistant_supported_languages}
            valueSelector="code"
            onChange={handleChangeSupportedLanguages}
          />
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            <HelpIconWrapper title="Enter domains to able to use company email addresses.">
              Allowed Domain(s)
              <RequiredComp />
            </HelpIconWrapper>
          </PalTypography>
          <PalTextField
            fullWidth
            multiline
            invalid={formik.touched[stepKey]?.allowed_mail_domains && formik.errors[stepKey]?.allowed_mail_domains}
            label="Enter explicit domains like example.com"
            maxRows={5}
            minRows={2}
            value={formik.values[stepKey].allowed_mail_domains}
            onChange={handleChangeTextField("allowed_mail_domains")}
          />
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Industry
            {/* <RequiredComp /> */}
          </PalTypography>
          <PalSelect
            fullWidth
            label="Select Industry"
            options={IndustryList}
            value={formik.values[stepKey].company_details?.industry}
            onChange={handleChangeCompanyDetailsOption("industry")}
          />
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Company Size
            {/* <RequiredComp /> */}
          </PalTypography>
          <PalSelect
            fullWidth
            label="Select Company Size"
            options={CompanySizeList}
            value={formik.values[stepKey].company_details?.size}
            onChange={handleChangeCompanyDetailsOption("size")}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Usage Count
          </PalTypography>
          <PalSelect
            fullWidth
            label="Select Usage Count"
            options={UsageCountList}
            value={formik.values[stepKey].company_details?.usage_count}
            onChange={handleChangeCompanyDetailsOption("usage_count")}
          />
        </Grid> */}
        <Grid item xs={6}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Department
            {/* <RequiredComp /> */}
          </PalTypography>
          <PalSelect
            fullWidth
            label="Select Department"
            options={DepartmentList}
            value={formik.values[stepKey].company_details?.department}
            onChange={handleChangeCompanyDetailsOption("department")}
          />
        </Grid>
        <Grid item xs={6}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Role
          </PalTypography>
          <PalSelect
            fullWidth
            label="Select Role"
            options={RoleList}
            value={formik.values[stepKey].company_details?.role}
            onChange={handleChangeCompanyDetailsOption("role")}
          />
        </Grid>
      </Grid>
    </StepContainer>
  );
}

export default StepGeneralInformation;
